export const DEFAULT_AVATAR = "/img/user_avatar.jpg";

export const DEFAULT_FILTERS = {
  search: "",
  roleId: "",
  sort: "nameAsc",
  perPage: 25,
};

export const ROLE_OPTIONS = [
  {
    id: 1,
    label: "Administrateur",
    shortLabel: "Admin",
    badge:
      "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200",
    dot: "bg-red-500",
  },
  {
    id: 2,
    label: "Starter",
    shortLabel: "Starter",
    badge:
      "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    dot: "bg-sky-500",
  },
  {
    id: 3,
    label: "Expert",
    shortLabel: "Expert",
    badge:
      "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-200",
    dot: "bg-blue-500",
  },
  {
    id: 5,
    label: "Coach",
    shortLabel: "Coach",
    badge:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200",
    dot: "bg-orange-500",
  },
  {
    id: 6,
    label: "Coach Pro",
    shortLabel: "Pro",
    badge:
      "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200",
    dot: "bg-amber-500",
  },
  {
    id: 7,
    label: "Coach Ultim",
    shortLabel: "Ultim",
    badge:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-200",
    dot: "bg-indigo-500",
  },
];

export function getRoleInfo(roleId) {
  return (
    ROLE_OPTIONS.find((role) => Number(role.id) === Number(roleId)) || {
      id: null,
      label: "Non affecté",
      shortLabel: "N/A",
      badge:
        "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/60",
      dot: "bg-slate-400",
    }
  );
}

export function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function formatUserDate(value) {
  if (!value) return "Date inconnue";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Date inconnue";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function getUserCreatedDate(user) {
  return user?.createdOn || user?.createdAt || user?.created_at || null;
}

export function getUserName(user) {
  return [user?.prenom, user?.nom].filter(Boolean).join(" ") || "Utilisateur";
}

export function getUserInitials(user) {
  const first = String(user?.prenom || "").charAt(0);
  const last = String(user?.nom || "").charAt(0);

  return `${first}${last}`.trim().toUpperCase() || "U";
}

export function getUserPhoto(user) {
  return user?.photo_url || user?.avatar_url || DEFAULT_AVATAR;
}

export function getUserAdherents(user) {
  if (Array.isArray(user?.adherents)) return user.adherents;
  if (Array.isArray(user?.Adherents)) return user.Adherents;
  return [];
}

export function getAdherentsLabel(user) {
  const adherents = getUserAdherents(user);

  if (!adherents.length) return "Aucun judoka";

  return adherents
    .map((adherent) =>
      [adherent?.prenom, adherent?.nom].filter(Boolean).join(" "),
    )
    .filter(Boolean)
    .join(", ");
}

export function filterUsers(users = [], filters = DEFAULT_FILTERS) {
  const search = normalizeText(filters.search);
  const roleId = String(filters.roleId || "");

  return users.filter((user) => {
    const name = normalizeText(`${user?.prenom || ""} ${user?.nom || ""}`);
    const email = normalizeText(user?.email);
    const role = normalizeText(getRoleInfo(user?.role_id).label);
    const adherents = normalizeText(getAdherentsLabel(user));

    const matchesSearch =
      !search ||
      name.includes(search) ||
      email.includes(search) ||
      role.includes(search) ||
      adherents.includes(search);

    const matchesRole =
      !roleId || String(user?.role_id || "") === String(roleId);

    return matchesSearch && matchesRole;
  });
}

export function sortUsers(users = [], sort = "nameAsc") {
  return [...users].sort((a, b) => {
    const nameA = normalizeText(getUserName(a));
    const nameB = normalizeText(getUserName(b));

    const dateA = new Date(getUserCreatedDate(a) || 0).getTime();
    const dateB = new Date(getUserCreatedDate(b) || 0).getTime();

    if (sort === "nameDesc") return nameB.localeCompare(nameA, "fr");
    if (sort === "recent") return dateB - dateA;
    if (sort === "oldest") return dateA - dateB;
    if (sort === "roleAsc") return Number(a?.role_id || 999) - Number(b?.role_id || 999);

    return nameA.localeCompare(nameB, "fr");
  });
}

export function paginateItems(items = [], page = 1, perPage = 25) {
  const safePerPage = Number(perPage || 25);
  const totalPages = Math.max(Math.ceil(items.length / safePerPage), 1);
  const safePage = Math.min(Math.max(Number(page || 1), 1), totalPages);
  const start = (safePage - 1) * safePerPage;

  return {
    page: safePage,
    totalPages,
    items: items.slice(start, start + safePerPage),
  };
}

export function buildUserStats(users = []) {
  const stats = {
    total: users.length,
    admins: 0,
    starters: 0,
    experts: 0,
    coachs: 0,
    coachPro: 0,
    coachUltim: 0,
    withoutAdherent: 0,
    withAdherent: 0,
  };

  users.forEach((user) => {
    const roleId = Number(user?.role_id);
    const adherentsCount = getUserAdherents(user).length;

    if (roleId === 1) stats.admins += 1;
    else if (roleId === 2) stats.starters += 1;
    else if (roleId === 3) stats.experts += 1;
    else if (roleId === 5) stats.coachs += 1;
    else if (roleId === 6) stats.coachPro += 1;
    else if (roleId === 7) stats.coachUltim += 1;

    if (adherentsCount > 0) stats.withAdherent += 1;
    else stats.withoutAdherent += 1;
  });

  return stats;
}